/**
 * Component to handle Welcome Messages
 *
 * They will be displayed to non-logged and logged users, once per session (cookie-based), based on settings
 * that are controlled from Squiz
 *
 * The module is acting upon receiving messageBus messages and does not require any other initialization
 * other than being included as a dependency
 *
 * @module app/components/WelcomeMessage
 */
import _ from 'lodash';
import deviceDetector from './DeviceDetector';
import jQuery from 'jquery';
import login from './Login';
import moment from 'moment-timezone';
import pageCommon from '../pages/PageCommon';
import StorageProvider from './StorageProvider';

/**
 * Seconds between next display of WelcomeMessage. Default is 24h (24 * 60 * 60)
 *
 * @type {Number}
 */
var timeBetween = 86400;

/**
 * Key for StorageProvider
 *
 * @type {String}
 */
var storageKey = 'welcomeMessages';

/**
 * Topics for MessageBus
 *
 * @type {Object}
 */
var messageBusTopics = {
    domReady: 'AppJs.domReady',
    loginTopic: 'gotLoginState',
    pageRendered: 'navigation.pageChanged',
};

/**
 * jQuery selector for the welcome message object
 *
 * @type {Object}
 */
var wmContainer = null;

/**
 * List of channels
 *
 * @type {Object}
 */
var channels = {
    all: 'all',
    web: 'web',
    native: 'native',
};

/**
 * Cookies object
 *
 * @type {Object}
 */
var cookies = {
    authenticated: 0,
    nonauthenticated: 0,
};

/**
 * Browser object with info on platform and channel
 *
 * @type {void}
 */
var browser = {
    platform: null,
    channel: null,
};

/**
 * Initialize cookies
 *
 * @returns {void}
 *
 * @private
 */
var initCookies = function initCookies() {

    if (window.WH && window.WH.sportsbook && window.WH.sportsbook.frontEndConfig &&
        window.WH.sportsbook.frontEndConfig.timeBetweenWM) {

        timeBetween = parseInt(window.WH.sportsbook.frontEndConfig.timeBetweenWM, 10);
    }

    cookies = JSON.parse(StorageProvider.get(storageKey));
};

/**
 * Set cookies with data
 *
 * @returns {void}
 *
 * @private
 */
var setCookies = function setCookies() {

    StorageProvider.set(storageKey, JSON.stringify(cookies));
};

/**
 * Set eligible value after displaying the message
 *
 * @param {Boolean} isLoggedIn Boolean with the log in state
 *
 * @private
 */
var setEligibleValue = function setEligibleValue(isLoggedIn) {

    if (isLoggedIn) {

        cookies.authenticated = Math.floor(new Date().getTime() / 1000);
    } else {

        cookies.nonauthenticated = Math.floor(new Date().getTime() / 1000);
    }
};

/**
 * Check if user is eligible to see welcome message
 *
 * @param {Boolean} isLoggedIn Boolean with the log in state
 *
 * @returns {Boolean}
 *
 * @private
 */
var isEligible = function isEligible(isLoggedIn) {

    return (isLoggedIn) ?
        !checkDisplayedState('authenticated') :
        !checkDisplayedState('nonauthenticated');
};

/**
 * Check value of cookie state
 *
 * @param {String} state Type of cookie
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkDisplayedState = function checkDisplayedState(state) {

    return !!(cookies[state] &&
        cookies[state] > (Math.floor(new Date().getTime() / 1000) - timeBetween));
};

/**
 * Get array of welcome messages
 *
 * @param {Boolean} isLoggedIn Boolean with the log in state
 *
 * @returns {Array}
 *
 * @private
 */
var getMessages = function getMessages(isLoggedIn) {

    if (isLoggedIn) {

        return window.WH.welcomeMessages.authenticated;
    } else {

        return window.WH.welcomeMessages.nonauthenticated;
    }
};

/**
 * Get a message that fulfills the requirements
 *
 * @param {Boolean} isLoggedIn Boolean with the log in state
 *
 * @returns {Object}
 *
 * @private
 */
var getMessage = function getMessage(isLoggedIn) {

    return _.chain(getMessages(isLoggedIn))
        .filter(filterMessages)
        .sortBy('priority')
        .first()
        .value();
};

/**
 * Check if message timeframe is now
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var isInTime = function isInTime(message) {

    var messageStartTime = moment.tz(message.displayRules.startTime, 'DD/MM/YYYY HH:mm', 'Europe/London'),
        messageEndTime = moment.tz(message.displayRules.endTime, 'DD/MM/YYYY HH:mm', 'Europe/London'),
        now = moment.tz(moment(), 'Europe/London');

    return !(now.isBefore(messageStartTime) || now.isAfter(messageEndTime));
};

/**
 * Check channel and platform
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkChannelPlatform = function checkChannelPlatform(message) {

    if (_.indexOf(message.displayRules.channel, channels.all) !== -1) {

        return checkPlatform(message);
    } else {

        return checkChannel(message);
    }
};

/**
 * Check channel
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkChannel = function checkChannel(message) {

    if (
        _.indexOf(message.displayRules.channel, browser.channel) !== -1 &&
        browser.channel === channels.web
    ) {

        return checkPlatform(message);
    }

    return checkNonWebChannel(message);
};

/**
 * Check non-web channel
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkNonWebChannel = function checkNonWebChannel(message) {

    if (
        _.indexOf(message.displayRules.channel, browser.platform) !== -1 &&
        browser.channel === channels.native
    ) {

        return true;
    }

    return false;
};

/**
 * Check platform
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkPlatform = function checkPlatform(message) {

    if (_.indexOf(message.displayRules.platform, channels.all) !== -1) {

        return true;
    }

    return checkSpecificPlatform(message);
};

/**
 * Check specific platform
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkSpecificPlatform = function checkSpecificPlatform(message) {

    if (_.indexOf(message.displayRules.platform, browser.platform) !== -1) {

        return true;
    }

    return false;
};

/**
 * Check business rules
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkBusinessRules = function checkBusinessRules(message) {

    if (!message.displayRules.businessRules) {

        return true;
    }

    return checkSpecificBusinessRules(message);
};

/**
 * Check specific business rules
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkSpecificBusinessRules = function checkSpecificBusinessRules(message) {

    if (!message.displayRules.businessRules.balance) {

        return true;
    }

    message.displayRules.businessRules.value = parseFloat(message.displayRules.businessRules.value);

    return !(!checkBalanceLt(message) && !checkBalanceEq(message) &&
        !checkBalanceHt(message));
};

/**
 * Business rule check: less than
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkBalanceLt = function checkBalanceLt(message) {

    return (message.displayRules.businessRules.operator === 'lt' &&
        login.getUserBalance() < message.displayRules.businessRules.value);
};

/**
 * Business rule check: equal to
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkBalanceEq = function checkBalanceEq(message) {

    return (message.displayRules.businessRules.operator === 'et' &&
        login.getUserBalance() === message.displayRules.businessRules.value);
};

/**
 * Business rule check: higher than
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var checkBalanceHt = function checkBalanceHt(message) {

    return (message.displayRules.businessRules.operator === 'ht' &&
        login.getUserBalance() > message.displayRules.businessRules.value);
};

/**
 * Rules to filter out messages
 *
 * @param {Object} message Object with WM to display
 *
 * @returns {Boolean}
 *
 * @private
 */
var filterMessages = function filterMessages(message) {

    return message.displayRules.active && isInTime(message) &&
        checkChannelPlatform(message) && checkBusinessRules(message);
};

/**
 * This function, in case there is a message to display, will push the data to the view and display it
 *
 * @param {Object} message Object with WM to display
 * @param {Boolean} isLoggedIn Boolean with the log in state
 *
 * @returns {void}
 *
 * @private
 */
var pushWelcomeMessage = function pushWelcomeMessage(message, isLoggedIn) {

    if (message) {

        setHtmlData(message);
        setEligibleValue(isLoggedIn);
    }
};

/**
 * Sets HTML data values for the message
 *
 * @param {Object} message Object with WM to display
 *
 * @private
 */
var setHtmlData = function setHtmlData(message) {

    wmContainer.find('h2.-head2').html(message.title);
    wmContainer.find('p').html(message.layout.text);
    wmContainer.find('.btn--cancel').html(message.layout.cancelButtonText)
        .off('click.cancel').on('click.cancel', function cancelButton(e) {

            e.preventDefault();
            wmContainer.removeClass('wh-show').addClass('wh-hide');

            return false;
        });
    wmContainer.find('.btn--success')
        .attr('href', message.layout.okButtonUrl).html(message.layout.okButtonText)
        .off('click.ok').on('click.ok', function okButton(e) {

            var sportsDomain = window.WH.sportsbook.sportsDomain;

            if (e.target.href.indexOf(sportsDomain + '/' + window.WH.sportsbook.routingKeyword) !== -1) {

                e.preventDefault();
                wmContainer.removeClass('wh-show').addClass('wh-hide');
                pageCommon.navigate(e.target.href);

                return false;
            }
        });
    wmContainer.removeClass('wh-hide').addClass('wh-show');
};

/**
 * Select and display welcome message if user is eligible for one
 *
 * @returns {void}
 *
 * @private
 */
var displayWelcomeMessage = function displayWelcomeMessage() {

    var isLoggedIn = login.isLoggedIn();

    if (isEligible(isLoggedIn)) {

        browser = deviceDetector.getPlatformCode();
        pushWelcomeMessage(getMessage(isLoggedIn), isLoggedIn);
    }

    setCookies();
};

/**
 * Sets WM Container
 *
 * @returns {void}
 *
 * @private
 */
var initWMContainer = function initWMContainer() {

    wmContainer = jQuery('#welcomeMessage');
};

/**
 * Initialize Welcome Messages, display any if user is eligible
 * Inits the controller after domReady message from App.js
 *
 * @returns {void}
 *
 * @private
 */
var initAfterMessage = function initAfterMessage() {

    if (window.WH && window.WH.welcomeMessages) {

        initWMContainer();
        initCookies();

        window.WH.messageBus.subscribe(messageBusTopics.pageRendered, displayWelcomeMessage);
        window.WH.messageBus.subscribe(messageBusTopics.loginTopic, displayWelcomeMessage);
    }
};

/**
 * Subscribe to domReady topic
 *
 * @returns {void}
 *
 * @private
 */
var init = function init() {

    window.WH.messageBus.subscribe(messageBusTopics.domReady, initAfterMessage);
};

init();
